import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight, Sparkles as Sparkle, ShieldCheck, Heart, Search, ClipboardList, Users, MessageCircle } from "lucide-react";
import { ASSETS } from "../lib/api";

/**
 * Pagina „Prima consultație" — punctul principal de intrare al pacientului
 * în cabinet. Design elegant, medical, cald. Fără formule agresive.
 */
export default function FirstConsultation() {
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-magical-hero overflow-hidden">
        <span aria-hidden className="absolute top-[10%] left-[6%] text-brand-yellow opacity-60"><Sparkle className="w-5 h-5"/></span>
        <span aria-hidden className="absolute top-[22%] right-[8%] text-brand-red opacity-50"><Heart className="w-4 h-4" fill="currentColor"/></span>

        <div className="max-w-6xl mx-auto container-x py-16 sm:py-24">
          <div className="grid lg:grid-cols-[1.35fr_1fr] gap-10 lg:gap-14 items-center">
            <div>
              <h1 className="font-display font-extrabold text-brand-navy text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]" data-testid="hero-title">
                Prima consultație – <span className="text-brand-red">primul pas către un plan de tratament construit împreună</span>
              </h1>

              <div className="mt-8 space-y-5 text-brand-navy/85 text-base sm:text-lg leading-relaxed font-light">
                <p><strong className="text-brand-navy">Tratamentul începe cu încrederea în medic. Iar încrederea începe cu prima consultație.</strong></p>
                <p>Prima consultație este etapa în care evaluăm starea de sănătate orală, stabilim diagnosticul și vă explicăm tratamentul necesar. Discutăm împreună opțiunile potrivite, etapele tratamentului și răspundem întrebărilor dumneavoastră, astfel încât decizia să fie luată în cunoștință de cauză.</p>
                <p>Consultația poate fi decontată prin <strong>Casa de Asigurări de Sănătate</strong>, în condițiile CAS.</p>
                <p className="font-display font-bold text-brand-navy text-xl sm:text-2xl pt-2">Clarificăm. Înțelegem. Alegem. Planificăm.</p>
              </div>

              <div className="mt-10">
                <Link
                  to="/programare?service=Prima%20consultație"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-brand-red text-white font-bold text-sm sm:text-base tracking-wide uppercase shadow-2xl hover:bg-brand-redSoft transition-colors"
                  data-testid="hero-book-button"
                >
                  <CalendarDays className="w-5 h-5" />
                  Solicită prima consultație
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <p className="mt-4 text-xs text-brand-navy/60 max-w-lg" data-testid="hero-cas-note">
                  Disponibilitatea serviciilor decontate prin CAS este stabilită conform condițiilor și fondurilor contractate.
                </p>
              </div>
            </div>

            {/* Portret Dr. Biriș — cald, primitor, în noul cabinet */}
            <div className="relative no-img-drag order-first lg:order-last" data-testid="first-consult-portrait-wrapper">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-brand-cream max-w-md mx-auto">
                <img
                  src={ASSETS.drSeatedPortrait}
                  alt="Dr. Cornelia Maria Biriș, medic primar stomatolog, primindu-vă în noul cabinet Ro-Dental Cluj-Napoca"
                  className="block w-full h-auto"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid="first-consult-portrait"
                />
              </div>
              <div className="hidden sm:flex absolute -bottom-4 left-4 items-center gap-2 bg-white/95 backdrop-blur rounded-full px-4 py-2 shadow-lg border border-brand-yellow/60">
                <Heart className="w-4 h-4 text-brand-red" fill="currentColor" />
                <span className="text-xs font-semibold text-brand-navy">Vă întâmpin cu drag</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DETAILED SECTION */}
      <section className="bg-white">
        <div className="max-w-3xl mx-auto container-x py-16 sm:py-24">
          <div className="text-center mb-12">
            <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.24em] mb-3">Ce înseamnă prima consultație</p>
            <h2 className="font-display font-extrabold text-brand-navy text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">
              Prima consultație – primul pas către un plan de tratament construit împreună
            </h2>
          </div>

          <div className="space-y-6 text-brand-navy/85 leading-[1.85] text-base sm:text-lg font-light" data-testid="detailed-content">
            <p><strong className="text-brand-navy">Tratamentul începe cu încrederea în medic. Iar încrederea începe cu prima consultație.</strong></p>

            <p>Prima consultație este momentul în care ne cunoaștem și înțelegem, în ansamblu, situația dumneavoastră.</p>

            <p>Pornim de la motivul pentru care ați venit la cabinet și de la ceea ce vă preocupă cel mai mult. Apoi evaluăm întreaga cavitate bucală, <strong>nu doar dintele sau problema punctuală</strong> pentru care v-ați prezentat.</p>

            <p>Examinăm sănătatea dinților, a gingiilor, a mucoaselor și a celorlalte structuri orale, precum și lucrările sau tratamentele stomatologice realizate anterior. Atunci când este necesar, completăm evaluarea cu investigații radiologice, fotografii, imagini realizate cu camera intraorală sau alte investigații potrivite cazului.</p>

            <p>Pentru că sănătatea orală face parte din sănătatea întregului organism, discutăm și despre starea dumneavoastră generală de sănătate: <em>antecedente medicale, afecțiuni existente, intervenții anterioare, tratamente medicamentoase, alergii</em> și orice alte informații care pot avea importanță pentru siguranța și alegerea tratamentului stomatologic.</p>

            <p className="font-display font-semibold text-brand-navy text-lg sm:text-xl pt-2 border-l-4 border-brand-yellow pl-5 not-italic">
              Dar o consultație nu înseamnă doar examinare.
            </p>

            <p>Vrem să înțelegem și <strong>ce vă doriți</strong>: ce vă deranjează, ce așteptări aveți, ce experiențe stomatologice ați avut până acum, ce temeri există și ce rezultat v-ar face să vă simțiți bine.</p>

            <p>Pe baza tuturor acestor informații, clarificăm problemele existente și discutăm variantele de tratament potrivite situației dumneavoastră.</p>

            <p>Vă explicăm, într-un limbaj cât mai clar:</p>
            <ul className="space-y-3 list-none pl-0">
              {[
                "ce probleme am identificat;",
                "ce trebuie tratat cu prioritate;",
                "ce variante de tratament există;",
                "avantajele și limitele fiecărei variante;",
                "etapele necesare;",
                "timpul aproximativ pe care îl presupune tratamentul;",
                "opțiunile care permit etapizarea tratamentului;",
                "costurile, în măsura în care acestea pot fi stabilite în această etapă.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-red shrink-0" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <p>Atunci când cazul o permite, putem folosi și <strong>imagini sau simulări digitale</strong> pentru a vă ajuta să vizualizați mai ușor situația actuală și o posibilă direcție a rezultatului dorit. Aceste simulări au <em>rol orientativ și de comunicare</em> și nu reprezintă o promisiune a rezultatului clinic final.</p>

            <h3 className="font-display font-bold text-brand-navy text-xl sm:text-2xl pt-8 flex items-center gap-2">
              <Users className="w-5 h-5 text-brand-red" /> Un plan construit împreună
            </h3>

            <p>Rolul medicului este să vă ghideze și să vă prezinte soluțiile medicale potrivite, cu avantajele și limitele lor.</p>

            <p><strong>Rolul dumneavoastră este la fel de important.</strong></p>

            <p>Ne dorim să înțelegeți opțiunile, să puteți pune întrebări și să participați în cunoștință de cauză la alegerea planului de tratament pe care îl vom urma împreună.</p>

            <p>Planul final va ține cont de nevoile medicale, starea generală de sănătate, așteptările dumneavoastră și posibilitățile reale de a parcurge tratamentul, păstrând întotdeauna <strong>siguranța și corectitudinea medicală</strong> pe primul loc.</p>

            <p>În cazurile complexe, tratamentul poate fi împărțit în etape și poate implica o echipă de specialiști. Important este să știm <em>de unde pornim, unde dorim să ajungem</em> și care este următorul pas.</p>

            <p className="font-display font-semibold text-brand-navy text-lg sm:text-xl pt-2 border-l-4 border-brand-red pl-5 not-italic">
              Prima consultație este începutul drumului.
            </p>

            <p>Un drum în care dumneavoastră nu sunteți doar beneficiarul tratamentului, ci <strong>parte din echipa care construiește rezultatul</strong>.</p>

            <p className="font-display font-bold text-brand-navy text-xl sm:text-2xl pt-4">
              Clarificăm. Înțelegem. Alegem. Planificăm.
            </p>
            <p>Și pornim împreună, pas cu pas, spre un zâmbet sănătos și în care să vă regăsiți.</p>
          </div>

          {/* Steps visual */}
          <div className="mt-14 grid sm:grid-cols-4 gap-4" data-testid="steps-visual">
            {[
              { icon: Search, label: "Clarificăm" },
              { icon: MessageCircle, label: "Înțelegem" },
              { icon: ClipboardList, label: "Alegem" },
              { icon: CalendarDays, label: "Planificăm" },
            ].map(({ icon: Icon, label }, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2 p-4">
                <div className="w-14 h-14 rounded-full bg-brand-cream border-2 border-brand-yellow/70 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-brand-red" />
                </div>
                <span className="font-display font-bold text-brand-navy text-base">{label}</span>
              </div>
            ))}
          </div>

          {/* CAS note discreet */}
          <div className="mt-12 rounded-2xl bg-brand-cream/60 border border-brand-yellow/40 p-5 flex items-start gap-3" data-testid="cas-note">
            <ShieldCheck className="w-5 h-5 text-brand-green shrink-0 mt-0.5" />
            <p className="text-sm text-brand-navy/80 leading-relaxed">
              Consultația poate fi <strong>decontată integral prin Casa de Asigurări de Sănătate Cluj</strong>, în condițiile CAS. Disponibilitatea serviciilor decontate este stabilită conform condițiilor și fondurilor contractate.
            </p>
          </div>

          {/* Final CTA */}
          <div className="mt-14 text-center" data-testid="final-cta">
            <Link
              to="/programare?service=Prima%20consultație"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-brand-red text-white font-bold text-sm sm:text-base tracking-wide uppercase shadow-2xl hover:bg-brand-redSoft transition-colors"
              data-testid="final-book-button"
            >
              <CalendarDays className="w-5 h-5" />
              Solicită prima consultație
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-4 text-xs text-brand-navy/60 max-w-md mx-auto">
              Următorul pas este alegerea unei zile potrivite din calendar. Vă vom contacta telefonic pentru a confirma intervalul orar.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
